/**
 * Hook: useEventsForRange
 * Fetches and subscribes to events for a visible month, with real-time updates.
 * Returns events indexed by date for quick badge lookup.
 */

import { useEffect, useState } from 'react';
import { subscribeEventsForRange, subscribeSharedEventsForRange, CalendarEvent } from '@/lib/eventStore';
import { getAuth } from 'firebase/auth';

const isPermissionError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    message.includes('Missing or insufficient permissions') ||
    message.includes('permission-denied')
  );
};

interface EventsByDate {
  [dateKey: string]: CalendarEvent[]; // 'YYYY-MM-DD'
}

export function useEventsForRange(
  startDate: Date,
  endDate: Date,
  dashboardId?: string
): EventsByDate {
  const auth = getAuth();
  const user = auth.currentUser;
  const [eventsByDate, setEventsByDate] = useState<EventsByDate>({});

  useEffect(() => {
    // Two live sources merged into one indexed map:
    //  - the public "shared" feed (visibility: 'shared') — visible to everyone, incl. logged-out
    //  - the current user's own events (only when signed in)
    // Own events win on id collisions so the owner sees the editable copy.
    let own: CalendarEvent[] = [];
    let shared: CalendarEvent[] = [];

    const emit = () => {
      const byId = new Map<string, CalendarEvent>();
      shared.forEach(e => { if (e.id) byId.set(e.id, e); });
      own.forEach(e => { if (e.id) byId.set(e.id, e); });
      const indexed: EventsByDate = {};
      byId.forEach(event => {
        const dateKey = event.start.toISOString().split('T')[0];
        (indexed[dateKey] ||= []).push(event);
      });
      setEventsByDate(indexed);
    };

    const onErr = (error: Error) => {
      if (isPermissionError(error)) return;
      console.error('[useEventsForRange] subscription error:', error);
    };

    const unsubs: Array<() => void> = [];
    unsubs.push(subscribeSharedEventsForRange(startDate, endDate, (events) => { shared = events; emit(); }, onErr));
    if (user) {
      unsubs.push(subscribeEventsForRange(user.uid, startDate, endDate, dashboardId, (events) => { own = events; emit(); }, onErr));
    }

    return () => unsubs.forEach(u => u());
  }, [user, startDate.toISOString(), endDate.toISOString(), dashboardId]);

  return eventsByDate;
}

/**
 * Hook: useEventsForDay
 * Get events for a specific day.
 */
export function useEventsForDay(date: Date, dashboardId?: string): CalendarEvent[] {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  const eventsByDate = useEventsForRange(dayStart, dayEnd, dashboardId);
  const dateKey = date.toISOString().split('T')[0];

  return eventsByDate[dateKey] || [];
}

/**
 * Hook: useEventPriorityBadge
 * Returns the highest-priority event badge state for a day.
 * Priority: cancelled (red) > confirmed (blue) > tentative (gray) > reminder (amber)
 */
export function useEventPriorityBadge(events: CalendarEvent[]): {
  color: 'red' | 'blue' | 'gray' | 'amber' | null;
  count: number;
  statuses: Record<string, number>;
} {
  if (!events.length) {
    return { color: null, count: 0, statuses: {} };
  }

  const statuses: Record<string, number> = {
    confirmed: 0,
    tentative: 0,
    cancelled: 0,
    reminder: 0,
  };

  events.forEach(event => {
    if (event.status === 'cancelled') {
      statuses.cancelled++;
    } else if (event.status === 'confirmed') {
      statuses.confirmed++;
    } else if (event.status === 'tentative') {
      statuses.tentative++;
    } else if (event.reminders?.length) {
      statuses.reminder++;
    }
  });

  let color: 'red' | 'blue' | 'gray' | 'amber' | null = null;
  if (statuses.cancelled > 0) color = 'red';
  else if (statuses.confirmed > 0) color = 'blue';
  else if (statuses.tentative > 0) color = 'gray';
  else if (statuses.reminder > 0) color = 'amber';

  return { color, count: events.length, statuses };
}
